import {
    h, Component, toChildArray, VNode, ComponentType, createRef
} from "preact";

import classname from "../../../../foundation/utils/classname";
import IconArrowRight from "../../Icons/IconArrowRight";
import MenuContext from "../MenuContext";
import withMenuItem, { toMenuItemChildren, WithMenuItemProps } from "../withMenuItem";

import "../style.css";

const EDGE_OFFSET = 12;

interface Props extends WithMenuItemProps {
    class?: string;
}

interface State {
    direction: "left" | "right";
}

class Submenu extends Component<Props, State> {
    static override contextType = MenuContext;

    override state: State = {
        direction: "right"
    };

    baseRef = createRef<HTMLButtonElement>();
    submenuRef = createRef<HTMLDivElement>();

    positionSubmenu = () => {
        if (!this.props.open) {
            return;
        }

        const menuRect = this.baseRef.current!.getBoundingClientRect();
        const submenuRect = this.submenuRef.current!.getBoundingClientRect();
        const direction = window.innerWidth >= menuRect.right + submenuRect.width + EDGE_OFFSET ? "right" : "left";
        let yTranslation = 0;

        const maxHeight = window.innerHeight - (EDGE_OFFSET * 2); // eslint-disable-line no-magic-numbers
        if (submenuRect.bottom > maxHeight) {
            const fullHeight = submenuRect.height;
            const scrollOffset = Math.max(fullHeight - maxHeight, 0);
            yTranslation = window.innerHeight - submenuRect.bottom - EDGE_OFFSET + scrollOffset;

            this.submenuRef.current!.style.maxHeight = `${maxHeight}px`;
        }

        if (yTranslation !== 0) {
            this.submenuRef.current!.style.transform = `translate3d(0, ${yTranslation}px, 0)`;
        }

        if (direction !== this.state.direction) {
            this.setState({
                direction
            });
            (this.context as MenuContext).lastSubmenuPosition = direction;
        }
    };

    override componentDidMount() {
        this.positionSubmenu();
    }

    override componentDidUpdate() {
        this.positionSubmenu();
    }

    render() {
        const { direction } = this.state;
        const {
            class: cssClass,
            content,
            highlighted,
            open,
            disabled,
            indicesPath,
            children,
            onMenuItemMouseEnter,
            onMenuItemMouseMove,
            handleMenuChildClick,
            handleMenuChildMouseEnter,
            handleMenuChildMouseMove
        } = this.props;
        const { highlightedIndicesPath, lastHighlightableOpen } = this.context;

        return (
            <button
                ref={this.baseRef}
                class={classname("menuButton sidebarSubmenu body-1 hasRightContent", { highlighted, subMenuOpen: open })}
                disabled={disabled}>
                <div class="mouseOverlay" onMouseEnter={onMenuItemMouseEnter} onMouseMove={onMenuItemMouseMove}/>
                <span class="content ellipsis">{content}</span>
                <div class="rightContent">
                    <IconArrowRight class="menuChevron"/>
                </div>
                <div
                    ref={this.submenuRef}
                    class={classname("submenu", direction, cssClass)}
                    style={{ visibility: open ? "visible" : "hidden" }}>
                    {toMenuItemChildren(toChildArray(children) as VNode[], {
                        parentIndicesPath: indicesPath,
                        highlightedIndicesPath,
                        lastHighlightableOpen,
                        handleMenuChildClick: handleMenuChildClick!,
                        handleMenuChildMouseEnter: handleMenuChildMouseEnter!,
                        handleMenuChildMouseMove
                    })}
                </div>
            </button>
        );
    }
}

const SubmenuAsMenuItem = withMenuItem(Submenu) as ComponentType<Props>;

export default Submenu;
export { SubmenuAsMenuItem };
